import { ArrowUpRight, Mail, MessageCircle, Phone, Send, type LucideIcon } from 'lucide-react';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { OfficeLocation } from '../../../types';
import './ChannelsSection.scss';

export interface ChannelsSectionProps {
  office: OfficeLocation | null;
  loading: boolean;
}

interface Channel {
  key: string;
  icon: LucideIcon;
  title: string;
  value: string;
  href: string;
  ctaKey: string;
}

function digits(v: string): string {
  return v.replace(/\D/g, '');
}

function tgHref(handle: string): string {
  const v = handle.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `https://t.me/${v.replace(/^@/, '')}`;
}

function buildChannels(office: OfficeLocation, t: (k: string) => string): Channel[] {
  const out: Channel[] = [];
  if (office.telegram) {
    out.push({
      key: 'telegram',
      icon: Send,
      title: t('contact.telegram'),
      value: office.telegram,
      href: tgHref(office.telegram),
      ctaKey: 'contacts.channels.open',
    });
  }
  if (office.whatsapp) {
    out.push({
      key: 'whatsapp',
      icon: MessageCircle,
      title: t('contact.whatsapp'),
      value: office.whatsapp,
      href: `https://wa.me/${digits(office.whatsapp)}`,
      ctaKey: 'contacts.channels.open',
    });
  }
  if (office.phone) {
    out.push({
      key: 'phone',
      icon: Phone,
      title: t('contact.phone'),
      value: office.phone,
      href: `tel:${office.phone.replace(/\s/g, '')}`,
      ctaKey: 'contacts.channels.call',
    });
  }
  if (office.email) {
    out.push({
      key: 'email',
      icon: Mail,
      title: t('contact.email'),
      value: office.email,
      href: `mailto:${office.email}`,
      ctaKey: 'contacts.channels.write',
    });
  }
  return out;
}

export default function ChannelsSection({ office, loading }: ChannelsSectionProps) {
  const { t } = useLang();
  const channels = office ? buildChannels(office, t) : [];
  const showSkeleton = loading && channels.length === 0;
  const empty = !loading && channels.length === 0;

  return (
    <section className="section section--tight contacts-channels">
      <div className="container">
        <div className="section-header contacts-channels__head">
          <span className="eyebrow">{t('contacts.channels.t')}</span>
          <h2 className="contacts-channels__title">{t('contacts.channels.sub')}</h2>
        </div>

        {empty ? (
          <EmptyState title={t('contacts.empty')} />
        ) : (
          <div className="contacts-channels__grid">
            {showSkeleton
              ? [0, 1, 2, 3].map((i) => (
                  <div key={i} className="card-frame contacts-channels__card">
                    <Skeleton width={56} height={56} shape="rect" />
                    <Skeleton width="70%" height={18} />
                    <Skeleton width="60%" height={14} />
                  </div>
                ))
              : channels.map((c) => {
                  const Icon = c.icon;
                  const isExternal = c.href.startsWith('http');
                  return (
                    <a
                      key={c.key}
                      href={c.href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noreferrer noopener' : undefined}
                      className={`card-frame card-hover contacts-channels__card contacts-channels__card--${c.key}`}
                    >
                      <span className="contacts-channels__icon" aria-hidden="true">
                        <Icon size={22} />
                      </span>
                      <span className="contacts-channels__label">{c.title}</span>
                      <span className="contacts-channels__value">{c.value}</span>
                      <span className="contacts-channels__cta">
                        <span>{t(c.ctaKey)}</span>
                        <ArrowUpRight size={14} />
                      </span>
                    </a>
                  );
                })}
          </div>
        )}
      </div>
    </section>
  );
}
